import React from "react";
import { Box } from "@mantine/core";
import StatStrip from "../../common/StatStrip";
import { ReportMetricDef } from "../../../types/reportsV2";
import { formatMetricValue } from "../../../utils/metricFormat";
import { MetricIndex, valueOf } from "./model/reportTree";
import MetricTooltip, { hasMetricHelp } from "./MetricTooltip";
import styles from "./ReportCanvas.module.css";

export interface ReportKpiStripProps {
  /** KPI metric keys, in view order. */
  kpis: string[];
  metricsByKey: ReadonlyMap<string, ReportMetricDef>;
  /** Totals of the latest run; null while loading or after an error. */
  totals: Array<number | null> | null;
  index: MetricIndex;
  /** A newer run is loading: the totals shown are the previous run's, so
   *  they are dimmed. */
  loading?: boolean;
}

/** The view's 1–4 summary totals, computed by the API for the whole range. */
export default function ReportKpiStrip({
  kpis,
  metricsByKey,
  totals,
  index,
  loading = false,
}: ReportKpiStripProps) {
  const stats = kpis
    .map((key) => metricsByKey.get(key))
    .filter((m): m is ReportMetricDef => !!m)
    .map((metric) => ({
      key: metric.key,
      label: metric.label,
      value: formatMetricValue(
        totals ? valueOf(totals, index, metric.key) : null,
        metric.format,
      ),
      info: hasMetricHelp(metric) ? (
        <MetricTooltip metric={metric} />
      ) : undefined,
    }));
  return (
    <Box
      className={styles.kpis}
      data-loading={loading || undefined}
      aria-busy={loading}
    >
      <StatStrip variant="card" stats={stats} />
    </Box>
  );
}
